import React__default from 'react';
import { Range, Transforms } from 'slate';
import { useSlateStatic, useFocused, useSelected } from 'slate-react';
import { Text, Spinner } from '@shopify/polaris';
import classNames from 'classnames';
import { isAndroidUserAgent } from '../../../../../../../../utilities/user-agent.mjs';
import styles from './Token.scss.mjs';
import { usePopoverContext, Popover } from '../../../../shared/components/Popover/Popover.mjs';
import { usePopover } from '../../../../../../../Popover/hooks/usePopover.mjs';
import { TextStyle } from '../../../../shared/components/TextStyle/TextStyle.mjs';
import { findNode } from '../../../../../../core/queries/findNode.mjs';

const defaultAttributes = {};
function Token({
  attributes,
  children,
  element,
  getAttributes,
  renderPopover,
  onClick,
  supportsBlockTokensBeta,
  enableExperimentalTabNavigation
}) {
  var _getAttributes;
  const editor = useSlateStatic();
  const {
    togglePopoverVisibility
  } = usePopoverContext();
  const {
    activatorAccessibilityProps,
    popoverAccessibilityProps
  } = usePopover({
    ariaHasPopup: 'menu'
  });
  const {
    selection
  } = editor;
  const focused = useFocused();
  const selected = useSelected();
  const active = focused && selected;
  const popoverActive = Boolean(renderPopover && active && selection && Range.isCollapsed(selection));
  const tokenAttributes = (_getAttributes = getAttributes === null || getAttributes === void 0 ? void 0 : getAttributes(element)) !== null && _getAttributes !== void 0 ? _getAttributes : defaultAttributes;
  const {
    icon: Icon,
    valid,
    warning,
    accessibilityLabel,
    loading,
    highlight,
    block,
    rightIcon: RightIcon
  } = tokenAttributes;
  const isAndroid = isAndroidUserAgent();
  const handleClick = () => {
    if (!selected && isAndroid) {
      const nodeEntry = findNode(editor, {
        at: [],
        match: node => node === element
      });
      if (nodeEntry) {
        const [, path] = nodeEntry;
        Transforms.select(editor, path);
      }
    }
    if (selected) {
      togglePopoverVisibility();
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(element);
  };
  const popover = renderPopover ? renderPopover(editor, element, tokenAttributes) : null;
  const label = tokenAttributes.label || element.value;
  const richLabel = tokenAttributes.richLabel;
  const androidAttributes = isAndroid ? {
    // COMPAT: Adding the `contentEditable` attribute on Android prevents
    // the keyboard from closing when backspacing near the edge of a void node
    // https://github.com/ianstormtaylor/slate/pull/5298
    root: {
      contentEditable: true,
      suppressContentEditableWarning: true
    },
    // COMPAT: We use a `:before` pseudo-element to render the label so that it is not part of the
    // `textContent` of the node. On Android, the `textContent` of the node gets inserted as
    // text when deleting near the edge of a void node when using GBoard.
    text: {
      'data-label': label
    }
  } : undefined;
  return /*#__PURE__*/React__default.createElement("span", androidAttributes === null || androidAttributes === void 0 ? void 0 : androidAttributes.root, children, /*#__PURE__*/React__default.createElement("span", Object.assign({}, attributes, activatorAccessibilityProps, {
    className: classNames(styles.Token, {
      [styles.selected]: active,
      [styles.loading]: loading,
      [styles.invalid]: valid === false,
      [styles.warning]: valid !== false && warning,
      [styles.highlight]: highlight,
      [styles.block]: supportsBlockTokensBeta && block
    }),
    onClick: handleClick,
    draggable: true
  }), Icon && !loading ? /*#__PURE__*/React__default.createElement(Icon, {
    className: styles.Icon,
    "aria-hidden": true
  }) : null, /*#__PURE__*/React__default.createElement(Text, {
    variant: "bodySm",
    as: "span",
    visuallyHidden: true
  }, accessibilityLabel), loading ? /*#__PURE__*/React__default.createElement(Spinner, {
    size: "small"
  }) : /*#__PURE__*/React__default.createElement(TextStyle, Object.assign({
    className: styles.Label,
    marks: element.children[0]
  }, androidAttributes === null || androidAttributes === void 0 ? void 0 : androidAttributes.text), isAndroid ? null : richLabel !== null && richLabel !== void 0 ? richLabel : label), RightIcon && !loading ? /*#__PURE__*/React__default.createElement(RightIcon, {
    className: classNames(styles.Icon, styles.right),
    "aria-hidden": true
  }) : null, popover ? /*#__PURE__*/React__default.createElement(Popover, Object.assign({
    active: popoverActive,
    accessibilityAnnouncement: popover.accessibilityAnnouncement,
    enableExperimentalTabNavigation: enableExperimentalTabNavigation
  }, popoverAccessibilityProps), popover.markup) : null));
}

export { Token };
